import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch, clearAuthState } from "./api";
import TwoFactorChallenge from "./TwoFactorChallenge";
import TwoFactorSetup from "./TwoFactorSetup";
import LanguageToggle from "./LanguageToggle";
import NotificationSettings from "./NotificationSettings";
import OtpInput from "./OtpInput";

type Method = "totp" | "email" | "backup";
type TwoFaStatus = {
  totpEnabled: boolean;
  emailOtpEnabled: boolean;
  requireOnLogin: boolean;
  backupCodesRemaining: number;
  mustSetUp: boolean;
  methods: Method[];
};

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("user") || "{}"));
  const [name, setName] = useState(user.name || "");
  const [email] = useState(user.email || "");
  const [recoveryEmail, setRecoveryEmail] = useState(user.recoveryEmail || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [twofa, setTwofa] = useState<TwoFaStatus | null>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);
  // A pending action that needs a 2FA token: given the token, it runs.
  const [pending, setPending] = useState<null | { methods: Method[]; run: (t: string) => void }>(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountConfirmInput, setDeleteAccountConfirmInput] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [accountDeleted, setAccountDeleted] = useState(false);
  const DELETE_ACCOUNT_CONFIRM_PHRASE = "Delete my account";
  // In-flight "confirm the code we just emailed you" step when turning email 2FA on.
  const [enableEmailState, setEnableEmailState] = useState<null | {
    devCode?: string;
    code: string;
    loading: boolean;
    error: string;
  }>(null);

  const loadStatus = async () => {
    const res = await apiFetch("/api/2fa/status");
    if (res?.ok) setTwofa(await res.json());
  };
  useEffect(() => {
    loadStatus();
  }, []);

  const saveProfile = async (twoFactorToken?: string) => {
    setLoading(true);
    setSuccess("");
    setError("");
    try {
      const res = await apiFetch(`/api/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, recoveryEmail, twoFactorToken }),
      });
      const data = await res.json();
      if (res.status === 403 && data.error === "2FA_REQUIRED") {
        setPending({ methods: data.methods, run: (t) => saveProfile(t) });
        setLoading(false);
        return;
      }
      if (!res.ok) setError(data.error || t("profile.failedUpdateName"));
      else {
        setSuccess(t("profile.profileUpdated"));
        setUser(data);
        localStorage.setItem("user", JSON.stringify(data));
      }
    } catch {
      setError(t("profile.failedUpdateName"));
    }
    setLoading(false);
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveProfile();
  };

  const savePassword = async (twoFactorToken: string) => {
    setLoading(true);
    setSuccess("");
    setError("");
    try {
      const res = await apiFetch(`/api/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, password: newPassword, twoFactorToken }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error === "2FA_REQUIRED" ? t("profile.twoFactorFailed") : data.error || t("profile.failedUpdatePassword"));
      else {
        setSuccess(t("profile.passwordUpdated"));
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch {
      setError(t("profile.failedUpdatePassword"));
    }
    setLoading(false);
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (newPassword !== confirmPassword) {
      setError(t("profile.passwordsNoMatch"));
      return;
    }
    setPending({ methods: twofa?.methods || ["email"], run: savePassword });
  };

  const setRequireOnLogin = async (value: boolean, twoFactorToken?: string) => {
    const res = await apiFetch("/api/2fa/settings", {
      method: "PATCH",
      body: JSON.stringify({ requireOnLogin: value, twoFactorToken }),
    });
    const data = await res!.json();
    if (res!.status === 403 && data.error === "2FA_REQUIRED") {
      setPending({ methods: data.methods, run: (t) => setRequireOnLogin(value, t) });
      return;
    }
    loadStatus();
  };

  const disableTotp = async (twoFactorToken?: string) => {
    const res = await apiFetch("/api/2fa/totp/disable", {
      method: "POST",
      body: JSON.stringify({ twoFactorToken }),
    });
    const data = await res!.json();
    if (res!.status === 403 && data.error === "2FA_REQUIRED") {
      setPending({ methods: data.methods, run: (t) => disableTotp(t) });
      return;
    }
    setSuccess(t("profile.authenticatorRemoved"));
    loadStatus();
  };

  const disableEmail = async (twoFactorToken?: string) => {
    const res = await apiFetch("/api/2fa/email/disable", {
      method: "POST",
      body: JSON.stringify({ twoFactorToken }),
    });
    const data = await res!.json();
    if (res!.status === 403 && data.error === "2FA_REQUIRED") {
      setPending({ methods: data.methods, run: (t) => disableEmail(t) });
      return;
    }
    setSuccess(t("profile.emailOtpOff"));
    loadStatus();
  };

  const enableEmail = async () => {
    const res = await apiFetch("/api/2fa/email/enable", { method: "POST" });
    const data = await res!.json();
    setEnableEmailState({ devCode: data.devCode, code: "", loading: false, error: "" });
  };

  const confirmEnableEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enableEmailState) return;
    setEnableEmailState({ ...enableEmailState, loading: true, error: "" });
    const c = await apiFetch("/api/2fa/email/confirm", {
      method: "POST",
      body: JSON.stringify({ code: enableEmailState.code.trim() }),
    });
    const data = await c!.json();
    if (c!.ok) {
      setEnableEmailState(null);
      setSuccess(t("profile.emailOtpOn"));
      loadStatus();
    } else {
      setEnableEmailState((s) => (s ? { ...s, loading: false, error: data.error || t("profile.invalidThatCode") } : s));
    }
  };

  const regenerate = async (twoFactorToken?: string) => {
    const res = await apiFetch("/api/2fa/backup-codes/regenerate", {
      method: "POST",
      body: JSON.stringify({ twoFactorToken }),
    });
    const data = await res!.json();
    if (res!.status === 403 && data.error === "2FA_REQUIRED") {
      setPending({ methods: data.methods, run: (t) => regenerate(t) });
      return;
    }
    setRegenCodes(data.backupCodes);
    loadStatus();
  };

  const deleteAccount = async (twoFactorToken: string) => {
    setDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      const res = await apiFetch("/api/account", {
        method: "DELETE",
        body: JSON.stringify({ confirm: deleteAccountConfirmInput, twoFactorToken }),
      });
      if (res && res.ok) {
        // The account (and its session cookie) are already gone server-side.
        // clearAuthState only clears this browser's UI-state cache of it.
        clearAuthState();
        setAccountDeleted(true);
        setTimeout(() => {
          window.location.href = "/login";
        }, 2500);
        return;
      }
      const data = await res?.json();
      setDeleteAccountError(data?.error || t("profile.deleteAccountFailed"));
    } catch {
      setDeleteAccountError(t("profile.deleteAccountFailed"));
    }
    setDeletingAccount(false);
    // The modal closed when the 2FA challenge opened, and the error renders
    // inside it, so reopen it or the failure is silent.
    setShowDeleteAccountModal(true);
  };

  // Closes the confirm-phrase modal and hands off to the same 2FA-challenge
  // flow every other sensitive action here uses (savePassword, disableTotp,
  // ...). Re-opening the modal after cancelling the challenge means retyping
  // the phrase, which is deliberate, not an oversight.
  const confirmDeleteAccount = () => {
    setShowDeleteAccountModal(false);
    setPending({ methods: twofa?.methods || ["email"], run: deleteAccount });
  };

  if (accountDeleted) {
    return (
      <div className="p-6 max-w-md mx-auto text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t("profile.accountDeletedTitle")}</h1>
        <p className="text-gray-600">{t("profile.accountDeletedBody")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-md mx-auto">
      {pending && (
        <TwoFactorChallenge
          methods={pending.methods}
          onCancel={() => setPending(null)}
          onVerified={(t) => {
            const run = pending.run;
            setPending(null);
            run(t);
          }}
        />
      )}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-red-700">{t("profile.deleteAccountModalTitle")}</h3>
            <p className="text-sm text-gray-600">{t("profile.deleteAccountModalWarning")}</p>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>{t("profile.deleteAccountModalItemRestaurants")}</li>
              <li>{t("profile.deleteAccountModalItemManagers")}</li>
              <li>{t("profile.deleteAccountModalItemBilling")}</li>
              <li>{t("profile.deleteAccountModalItemAccount")}</li>
            </ul>
            <p className="text-sm text-gray-700">
              {t("profile.deleteAccountTypeToConfirm")}{" "}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-red-700">
                {DELETE_ACCOUNT_CONFIRM_PHRASE}
              </code>
            </p>
            <input
              type="text"
              value={deleteAccountConfirmInput}
              onChange={(e) => setDeleteAccountConfirmInput(e.target.value)}
              disabled={deletingAccount}
              className="w-full border rounded px-3 py-2"
              autoFocus
            />
            <p className="text-xs text-gray-500">{t("profile.deleteAccountTwoFactorNote")}</p>
            {deleteAccountError && <div className="text-sm text-red-600">{deleteAccountError}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteAccountConfirmInput("");
                  setDeleteAccountError(null);
                }}
                disabled={deletingAccount}
                className="flex-1 py-2 rounded border font-semibold text-gray-700"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={confirmDeleteAccount}
                disabled={deletingAccount || deleteAccountConfirmInput !== DELETE_ACCOUNT_CONFIRM_PHRASE}
                className="flex-1 bg-red-600 text-white py-2 rounded font-semibold disabled:opacity-50"
              >
                {deletingAccount ? t("profile.deletingAccount") : t("profile.deleteAccountButton")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSetup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <TwoFactorSetup
            onCancel={() => setShowSetup(false)}
            onDone={() => {
              setShowSetup(false);
              loadStatus();
            }}
          />
        </div>
      )}
      {enableEmailState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={confirmEnableEmail} className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm space-y-4">
            <h3 className="text-lg font-bold text-gray-900">{t("profile.confirmEmailTitle")}</h3>
            <p className="text-sm text-gray-600">{t("twofaChallenge.enterEmailCode")}</p>
            {enableEmailState.devCode && (
              <p className="text-sm text-center text-gray-500">
                {t("twofaChallenge.devModeCode", { code: enableEmailState.devCode })}
              </p>
            )}
            <OtpInput
              value={enableEmailState.code}
              onChange={(v) => setEnableEmailState((s) => (s ? { ...s, code: v } : s))}
              disabled={enableEmailState.loading}
            />
            {enableEmailState.error && <div className="text-sm text-center text-red-600">{enableEmailState.error}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEnableEmailState(null)}
                className="flex-1 py-2 rounded border font-semibold text-gray-700"
              >
                {t("twofaChallenge.cancel")}
              </button>
              <button
                type="submit"
                disabled={enableEmailState.loading || enableEmailState.code.trim().length < 6}
                className="flex-1 bg-green-600 text-white py-2 rounded font-semibold disabled:opacity-50"
              >
                {enableEmailState.loading ? t("twofaChallenge.verifying") : t("twofaChallenge.verify")}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex flex-col items-center mb-6">
        <span className="text-lg font-semibold">{user.name}</span>
        <span className="text-xs text-gray-500">{t(`roles.${user.role}`, { defaultValue: user.role })}</span>
      </div>

      <div className="mb-6 bg-white rounded-xl shadow p-4 flex items-center justify-between">
        <div>
          <div className="font-medium">{t("common.language")}</div>
          <div className="text-xs text-gray-500">{t("common.languageDescription")}</div>
        </div>
        <LanguageToggle />
      </div>

      {(user.role === "admin" || user.role === "manager") && <NotificationSettings />}

      <form onSubmit={handleProfileSubmit} className="mb-6 bg-white rounded-xl shadow p-4">
        <label className="block mb-2 font-medium">{t("profile.name")}</label>
        <input
          type="text"
          className="w-full border rounded px-3 py-2 mb-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
        />
        <label className="block mb-2 font-medium">{t("profile.email")}</label>
        <input type="email" className="w-full border rounded px-3 py-2 mb-2 bg-gray-100" value={email} disabled />
        <label className="block mb-2 font-medium">{t("profile.recoveryEmail")}</label>
        <p className="text-xs text-gray-500 mb-2">{t("profile.recoveryEmailDescription")}</p>
        <input
          type="email"
          className="w-full border rounded px-3 py-2 mb-2"
          placeholder={t("profile.recoveryEmailPlaceholder")}
          value={recoveryEmail}
          onChange={(e) => setRecoveryEmail(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="w-full bg-teal-600 text-white py-2 rounded font-semibold mt-2 disabled:opacity-50"
          disabled={loading || !name}
        >
          {t("profile.saveChanges")}
        </button>
      </form>

      <form onSubmit={handlePasswordChange} className="bg-white rounded-xl shadow p-4 mb-6">
        <label className="block mb-2 font-medium">{t("profile.changePassword")}</label>
        <p className="text-xs text-gray-500 mb-2">{t("profile.twoFactorPrompt")}</p>
        <input
          type="password"
          className="w-full border rounded px-3 py-2 mb-2"
          placeholder={t("profile.currentPassword")}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={loading}
        />
        <input
          type="password"
          className="w-full border rounded px-3 py-2 mb-2"
          placeholder={t("profile.newPassword")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
          minLength={8}
        />
        <input
          type="password"
          className="w-full border rounded px-3 py-2 mb-2"
          placeholder={t("profile.confirmNewPassword")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="w-full bg-green-600 text-white py-2 rounded font-semibold mt-2 disabled:opacity-50"
          disabled={loading || !currentPassword || !newPassword || !confirmPassword}
        >
          {t("profile.updatePassword")}
        </button>
      </form>

      {/* Two-factor authentication */}
      <div className="bg-white rounded-xl shadow p-4 space-y-4">
        <div className="flex items-center justify-between">
          <label className="font-medium">{t("profile.twoFactorAuth")}</label>
          {twofa?.mustSetUp && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{t("profile.requiredForRole")}</span>
          )}
        </div>

        {/* Authenticator app */}
        <div className="flex items-center justify-between text-sm">
          <div>
            <div className="font-medium">{t("profile.authenticatorApp")}</div>
            <div className="text-gray-500">
              {twofa?.totpEnabled
                ? t("profile.backupCodesLeft", { count: twofa.backupCodesRemaining })
                : t("profile.off")}
            </div>
          </div>
          {twofa?.totpEnabled ? (
            <button onClick={() => disableTotp()} className="text-red-600 underline">
              {t("profile.remove")}
            </button>
          ) : (
            <button onClick={() => setShowSetup(true)} className="text-teal-600 underline">
              {t("profile.setUp")}
            </button>
          )}
        </div>

        {/* Email 2FA */}
        <div className="flex items-center justify-between text-sm">
          <div>
            <div className="font-medium">{t("profile.emailCode")}</div>
            <div className="text-gray-500">{twofa?.emailOtpEnabled ? t("profile.on") : t("profile.off")}</div>
          </div>
          {twofa?.emailOtpEnabled ? (
            <button onClick={() => disableEmail()} className="text-red-600 underline">
              {t("profile.turnOff")}
            </button>
          ) : (
            <button onClick={enableEmail} className="text-teal-600 underline">
              {t("profile.turnOn")}
            </button>
          )}
        </div>

        {/* Require on login */}
        {(twofa?.totpEnabled || twofa?.emailOtpEnabled) && (
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="font-medium">{t("profile.requireEveryLogin")}</span>
            <input
              type="checkbox"
              checked={!!twofa?.requireOnLogin}
              onChange={(e) => setRequireOnLogin(e.target.checked)}
              className="w-4 h-4"
            />
          </label>
        )}

        {twofa?.totpEnabled && (
          <button onClick={() => regenerate()} className="text-teal-600 underline text-sm">
            {t("profile.regenerateBackupCodes")}
          </button>
        )}

        {regenCodes && (
          <div className="bg-stone-50 rounded p-3 text-sm">
            <p className="text-gray-600 mb-1">{t("profile.newBackupCodes")}</p>
            <div className="grid grid-cols-2 gap-1 font-mono">
              {regenCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {user.role === "admin" && (
        <div className="mt-6 bg-white rounded-xl shadow p-4 border border-red-200">
          <h2 className="font-semibold text-red-700 mb-1">{t("profile.dangerZone")}</h2>
          <p className="text-sm text-gray-600 mb-3">{t("profile.deleteAccountWarning")}</p>
          <button
            onClick={() => setShowDeleteAccountModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded font-semibold hover:bg-red-700"
          >
            {t("profile.deleteAccountButton")}
          </button>
        </div>
      )}

      {(success || error) && (
        <div className={`mt-4 text-center font-semibold ${success ? "text-green-600" : "text-red-600"}`}>
          {success || error}
        </div>
      )}
    </div>
  );
};

export default Profile;
